import { useState } from "react";
import { IonCheckbox, IonInput, IonItem, IonList } from "@ionic/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Toast } from "src/components/atoms";
import { Button, Icon } from "swing-components";

import { PUT, translate } from "~utils";

type SubDisinterestedModalProps = { onDismiss: () => void; requestId: string };

export function SubDisinterestedModal(props: SubDisinterestedModalProps) {
  const { onDismiss, requestId } = props;

  const reasonValues = {
    pay: "SUB_MATCH_FEEDBACK_REASON_PAY",
    location: "SUB_MATCH_FEEDBACK_REASON_LOCATION",
    grade: "SUB_MATCH_FEEDBACK_REASON_GRADE",
    subject: "SUB_MATCH_FEEDBACK_REASON_SUBJECT",
    details: "SUB_MATCH_FEEDBACK_REASON_JOB_DETAILS",
    time: "SUB_MATCH_FEEDBACK_REASON_TIME",
    other: "SUB_MATCH_FEEDBACK_REASON_OTHER",
  };

  const disinterestedReasons = [
    {
      value: reasonValues.pay,
      label: translate("SUB_DISINTERESTED_PAY_TOO_LOW"),
    },
    {
      value: reasonValues.location,
      label: translate("SUB_DISINTERESTED_TOO_FAR"),
    },
    {
      value: reasonValues.grade,
      label: translate("SUB_DISINTERESTED_GRADE_FIT"),
    },
    {
      value: reasonValues.subject,
      label: translate("SUB_DISINTERESTED_SUBJECT_FIT"),
    },
    {
      value: reasonValues.details,
      label: translate("SUB_DISINTERESTED_JOB_DETAILS"),
    },
    {
      value: reasonValues.time,
      label: translate("SUB_DISINTERESTED_TIME_FIT"),
    },
    {
      value: reasonValues.other,
      label: translate("SUB_DISINTERESTED_OTHER"),
    },
  ];

  const [selectedReasons, setSelectedReasons] = useState([] as string[]);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showFailureToast, setShowFailureToast] = useState(false);
  const [customReason, setCustomReason] = useState("");

  const onCheckboxChange = (newReason: string) => {
    setSelectedReasons((old) => {
      const isSelecting = !old.includes(newReason);
      const isOther = newReason === reasonValues.other;

      if (!isSelecting) {
        return old.filter((r) => r !== newReason);
      }

      if (isOther) {
        return [reasonValues.other];
      }

      const withoutOther = old.filter((r) => r !== reasonValues.other);
      return [...withoutOther, newReason];
    });
  };

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: async (payload: any) => {
      const res = await PUT("/api/sub/request/{requestId}", payload, {
        pathParams: { requestId },
      });
      setShowSuccessToast(true);
      await queryClient.invalidateQueries().then(() => onDismiss());
      return res;
    },
    onError: () => setShowFailureToast(true),
  });

  return (
    <div
      css={{
        position: "relative",
        overflow: "scroll",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Toast
        isVisible={showSuccessToast}
        type="positive"
        onDismiss={() => setShowSuccessToast(false)}
        text={translate("SUB_DISINTERESTED_SUCCESS")}
      />
      <Toast
        isVisible={showFailureToast}
        type="negative"
        onDismiss={() => setShowFailureToast(false)}
        text={translate("LABEL_SOMETHING_WENT_WRONG")}
      />

      <div
        css={{
          position: "sticky",
          top: 0,
          padding: "32px 16px 16px 16px",
          background: "white",
          display: "flex",
          justifyContent: "space-between",
          zIndex: 999,
        }}
      >
        <div>
          <p css={{ fontSize: 20, marginBottom: 16, marginTop: 0 }}>
            {translate("SUB_DISINTERESTED_MODAL_HEADER")}
          </p>
          <span css={{ fontSize: 14 }}>{translate("SUB_DISINTERESTED_MODAL_SUBHEADER")}</span>
        </div>
        <div css={{ cursor: "pointer" }} onClick={onDismiss}>
          <Icon name="Close" color="var(--swing-blue500)" />
        </div>
      </div>
      <IonList>
        {disinterestedReasons.map((item) => {
          const shouldShowCustomReason =
            (item.value === reasonValues.pay || item.value === reasonValues.other) &&
            selectedReasons.includes(item.value);
          const placeholderText =
            item.value === reasonValues.pay
              ? translate("BAD_FIT_PLACEHOLDER_PAY")
              : translate("BAD_FIT_PLACEHOLDER_CUSTOM");
          return (
            <>
              <IonItem key={item.value}>
                <IonCheckbox
                  checked={selectedReasons.includes(item.value)}
                  onIonChange={() => onCheckboxChange(item.value)}
                  labelPlacement="end"
                >
                  {item.label}
                </IonCheckbox>
              </IonItem>
              {shouldShowCustomReason && (
                <IonItem>
                  <IonInput
                    placeholder={placeholderText}
                    value={customReason}
                    onIonInput={(e) => setCustomReason(e.detail.value || "")}
                  />
                </IonItem>
              )}
            </>
          );
        })}
      </IonList>
      <div
        css={{
          position: "fixed",
          bottom: 0,
          padding: "16px",
          background: "white",
          display: "flex",
          gap: 16,
          width: "100%",
        }}
      >
        <Button
          isFluid
          status={selectedReasons.length === 0 || isPending ? "disabled" : "enabled"}
          onClick={() => {
            mutate({
              action: "GIVE_MATCH_FEEDBACK",
              feedbackScore: 0,
              feedbackMatchReasons: selectedReasons,
              feedbackCustomReason: customReason,
            });
          }}
        >
          {translate("LABEL_SUBMIT")}
        </Button>
      </div>
    </div>
  );
}
